import { AnalyticsDataPoint, AnalyticsChartData, StatisticsSummary, TimeAggregation } from '../types/analytics';

export const POWER_METRICS = [
  {
    key: 'ac_power',
    label: 'A/C Power',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#0E7EE4',
    deviceId: 'ac_subsystem',
    datapoint: 'power'
  },
  {
    key: 'grid_import',
    label: 'Grid Import',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#7D3BBF',
    deviceId: 'solar_subsystem',
    datapoint: 'grid_power'
  },
  {
    key: 'solar_power',
    label: 'Solar Power',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#F2AA32',
    deviceId: 'solar_subsystem',
    datapoint: 'power'
  },
  {
    key: 'expected_solar',
    label: 'Expected Solar Power',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#FFD700',
    deviceId: 'solar_subsystem',
    datapoint: 'expected_power'
  },
  {
    key: 'total_power',
    label: 'Total Power',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#000000',
    deviceId: 'calculated',
    datapoint: 'calculated'
  },
  {
    key: 'non_ac_power',
    label: 'Non A/C Power',
    unit: 'kW',
    yAxis: 'primary' as const,
    color: '#9CA3AF',
    deviceId: 'calculated',
    datapoint: 'calculated'
  }
];

export const TEMPERATURE_METRICS = [
  {
    key: 'outdoor_temp',
    label: 'Outdoor Temp',
    unit: '°C',
    yAxis: 'secondary' as const,
    color: '#109095',
    deviceId: 'site_dynamic', // Will be replaced with actual siteId at runtime
    datapoint: 'outdoor_temperature'
  },
  {
    key: 'indoor_temp',
    label: 'Indoor Temp',
    unit: '°C',
    yAxis: 'secondary' as const,
    color: '#14B0BC',
    deviceId: 'zone_main',
    datapoint: 'avg_room_temperature'
  }
];

export const TIME_AGGREGATIONS: Record<string, TimeAggregation[]> = {
  'power-temp': [
    { key: '1min', label: '1 min', value: '1T', maxDays: 1 },
    { key: '5min', label: '5 min', value: '5T', maxDays: 7 },
    { key: '15min', label: '15 min', value: '15T', maxDays: 30 },
    { key: '60min', label: '60 min', value: '60T', maxDays: 90 }
  ],
  'energy': [
    { key: 'hour', label: 'Hour', value: '1H', maxDays: 7 },
    { key: 'day', label: 'Day', value: '1D', maxDays: 90 },
    { key: 'week', label: 'Week', value: '1W', maxDays: 365 },
    { key: 'month', label: 'Month', value: '1M', maxDays: 1095 }
  ]
};

function generateCompleteTimeRange(startDate: string, endDate: string, aggregation: string): string[] {
  const timestamps: string[] = [];
  const start = new Date(startDate + 'T00:00:00.000Z');
  const end = new Date(endDate + 'T23:59:59.999Z');
  
  let current = new Date(start);
  let intervalMs: number;
  
  // Determine interval in milliseconds based on aggregation
  switch (aggregation) {
    case '1min':
    case '1T':
      intervalMs = 60 * 1000; // 1 minute
      break;
    case '5min':
    case '5T':
      intervalMs = 5 * 60 * 1000; // 5 minutes
      break;
    case '15min':
    case '15T':
      intervalMs = 15 * 60 * 1000; // 15 minutes
      break;
    case '60min':
    case '60T':
      intervalMs = 60 * 60 * 1000; // 60 minutes
      break;
    case '1hour':
    case '1H':
    case 'hour':
      intervalMs = 60 * 60 * 1000; // 1 hour
      break;
    case '1D':
    case 'day':
      intervalMs = 24 * 60 * 60 * 1000; // 1 day
      break;
    case '1W':
    case 'week':
      intervalMs = 7 * 24 * 60 * 60 * 1000; // 1 week
      break;
    case '1M':
    case 'month':
      intervalMs = 30 * 24 * 60 * 60 * 1000; // 1 month (approximate)
      break;
    default:
      intervalMs = 60 * 60 * 1000; // Default to 1 hour
  }
  
  while (current <= end) {
    timestamps.push(current.toISOString());
    current = new Date(current.getTime() + intervalMs);
  }
  
  return timestamps;
}

export function getAllCalculatedData(data: AnalyticsDataPoint[]): AnalyticsDataPoint[] {
  if (!data.length) return [];
  
  // Group data by timestamp
  const timeMap = new Map<string, Record<string, number>>();
  
  data.forEach(point => {
    if (!timeMap.has(point.timestamp)) {
      timeMap.set(point.timestamp, {});
    }
    timeMap.get(point.timestamp)![point.metric] = point.value;
  });
  
  const allData: AnalyticsDataPoint[] = [...data];
  
  // Calculate derived metrics for each timestamp
  Array.from(timeMap.keys()).forEach(timestamp => {
    const timeData = timeMap.get(timestamp) || {};
    
    // Calculate Total Power = Solar Power + Grid Import
    const solarPower = timeData['solar_power'];
    const gridImport = timeData['grid_import'];
    const acPower = timeData['ac_power'];
    
    // Only calculate total power if we have at least one of the base metrics
    if (solarPower !== undefined || gridImport !== undefined) {
      const solarValue = Number(solarPower) || 0;
      const gridValue = Number(gridImport) || 0;
      const totalPower = solarValue + gridValue;
      
      // Only add if values are valid numbers and not extreme
      if (isFinite(totalPower) && totalPower >= 0 && totalPower < 10000) {
        allData.push({
          timestamp,
          value: totalPower,
          metric: 'total_power'
        });
        
        // Calculate Non A/C Power = Total Power - A/C Power
        if (acPower !== undefined) {
          const acValue = Number(acPower) || 0;
          const nonAcPower = totalPower - acValue;
          
          // Only add if values are valid numbers and not extreme
          if (isFinite(nonAcPower) && nonAcPower >= 0 && nonAcPower < 10000) {
            allData.push({
              timestamp,
              value: nonAcPower,
              metric: 'non_ac_power'
            });
          }
        }
      }
    }
  });
  
  return allData;
}

export function getDaysDifference(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getAvailableAggregations(
  mode: 'power-temp' | 'energy',
  startDate: string,
  endDate: string
): TimeAggregation[] {
  const daysDiff = getDaysDifference(startDate, endDate);
  const aggregations = TIME_AGGREGATIONS[mode];
  
  return aggregations.filter(agg => daysDiff <= agg.maxDays);
}

export function getDefaultAggregation(
  mode: 'power-temp' | 'energy',
  startDate: string,
  endDate: string
): string {
  const available = getAvailableAggregations(mode, startDate, endDate);
  
  if (available.length === 0) {
    return TIME_AGGREGATIONS[mode][TIME_AGGREGATIONS[mode].length - 1].key;
  }
  
  const daysDiff = getDaysDifference(startDate, endDate);
  
  if (mode === 'power-temp') {
    if (daysDiff <= 1) return '5min';
    if (daysDiff <= 7) return '15min';
    return '60min';
  } else {
    if (daysDiff <= 7) return 'hour';
    if (daysDiff <= 30) return 'day';
    if (daysDiff <= 90) return 'week';
    return 'month';
  }
}

export function transformDataForChart(
  data: AnalyticsDataPoint[],
  selectedMetrics: string[],
  dateRange?: { start: string; end: string },
  timeAggregation?: string
): AnalyticsChartData {
  // Group data by timestamp
  const timeMap = new Map<string, Record<string, number>>();
  
  data.forEach(point => {
    if (!timeMap.has(point.timestamp)) {
      timeMap.set(point.timestamp, {});
    }
    timeMap.get(point.timestamp)![point.metric] = point.value;
  });

  // Use actual data timestamps, but ensure they're sorted
  const timestamps = Array.from(timeMap.keys()).sort();
  
  if (timestamps.length === 0) {
    return { timestamps: [], series: [] };
  }
  
  // Calculate all derived metrics first (regardless of selection)
  const fullDataMap = new Map<string, Record<string, number>>();
  
  timestamps.forEach(timestamp => {
    const timeData = timeMap.get(timestamp) || {};
    const fullTimeData = { ...timeData };
    
    // Calculate Total Power = Solar Power + Grid Import
    const solarPower = timeData['solar_power'];
    const gridImport = timeData['grid_import'];
    const acPower = timeData['ac_power'];
    
    // Only calculate total power if we have at least one of the base metrics
    if (solarPower !== undefined || gridImport !== undefined) {
      const solarValue = Number(solarPower) || 0;
      const gridValue = Number(gridImport) || 0;
      const totalPower = solarValue + gridValue;
      
      // Only add if values are valid and reasonable
      if (isFinite(totalPower) && totalPower >= 0 && totalPower < 10000) {
        fullTimeData['total_power'] = totalPower;
        
        // Calculate Non A/C Power = Total Power - A/C Power
        if (acPower !== undefined) {
          const acValue = Number(acPower) || 0;
          const nonAcPower = totalPower - acValue;
          
          // Only add if values are valid and reasonable
          if (isFinite(nonAcPower) && nonAcPower >= 0 && nonAcPower < 10000) {
            fullTimeData['non_ac_power'] = nonAcPower;
          }
        }
      }
    }
    // If we don't have base metrics, don't add total_power (it will be null)
    
    fullDataMap.set(timestamp, fullTimeData);
  });
  
  // Create series only for selected metrics
  const series = selectedMetrics.map(metricKey => {
    const metricConfig = [...POWER_METRICS, ...TEMPERATURE_METRICS].find(m => m.key === metricKey);
    if (!metricConfig) return null;

    const seriesData = timestamps.map(timestamp => {
      const value = fullDataMap.get(timestamp)?.[metricKey];
      // Return null for missing data, 0 for actual zero values, valid numbers for data
      if (value === undefined || value === null) {
        return null; // Missing data - will show as gap in chart
      }
      // Ensure we return valid numbers only
      return isFinite(value) && value >= 0 && value < 10000 ? value : null;
    });

    return {
      name: metricConfig.label,
      data: seriesData,
      unit: metricConfig.unit,
      color: metricConfig.color,
      yAxisIndex: metricConfig.yAxis === 'primary' ? 0 : 1
    };
  }).filter(Boolean) as AnalyticsChartData['series'];

  return { timestamps, series };
}

export function convertPowerToEnergy(
  powerData: AnalyticsDataPoint[],
  aggregation: string
): AnalyticsDataPoint[] {
  // Convert power (kW) to energy (kWh) based on time aggregation
  const conversionFactor = getEnergyConversionFactor(aggregation);
  
  return powerData.map(point => ({
    ...point,
    value: point.value * conversionFactor
  }));
}

function getEnergyConversionFactor(aggregation: string): number {
  // Convert power readings to energy based on time interval
  switch (aggregation) {
    case '1min': return 1 / 60; // 1 minute = 1/60 hour
    case '5min': return 5 / 60; // 5 minutes = 5/60 hour
    case '15min': return 15 / 60; // 15 minutes = 15/60 hour
    case '60min': return 1; // 60 minutes = 1 hour
    case '1hour': case 'hour': return 1; // 1 hour = 1 hour
    case 'day': return 24; // 1 day = 24 hours (average)
    case 'week': return 168; // 1 week = 168 hours (average)
    case 'month': return 730; // 1 month ≈ 730 hours (average)
    default: return 1;
  }
}

export function calculateStatistics(
  data: AnalyticsDataPoint[],
  mode: 'power-temp' | 'energy'
): StatisticsSummary {
  // Get all calculated data including derived metrics
  const allCalculatedData = getAllCalculatedData(data);
  
  // Group data by metric
  const metricGroups = allCalculatedData.reduce((acc, point) => {
    if (!acc[point.metric]) acc[point.metric] = [];
    acc[point.metric].push(point);
    return acc;
  }, {} as Record<string, AnalyticsDataPoint[]>);

  if (mode === 'energy') {
    // Calculate totals for energy metrics - use ac_power for AC consumption
    const totalEnergyConsumption = sumMetricValues(metricGroups['ac_power'] || []);
    const totalSolarGeneration = sumMetricValues(metricGroups['solar_power'] || []);
    const totalGridImport = sumMetricValues(metricGroups['grid_import'] || []);

    // Calculate temperature statistics for business hours (9:00-17:00)
    const indoorTempBusinessHours = filterBusinessHours(metricGroups['indoor_temp'] || []);
    const outdoorTempBusinessHours = filterBusinessHours(metricGroups['outdoor_temp'] || []);

    return {
      totalEnergyConsumption,
      totalSolarGeneration,
      totalGridImport,
      avgIndoorTempBusinessHours: calculateAverage(indoorTempBusinessHours),
      maxOutdoorTempBusinessHours: calculateMax(outdoorTempBusinessHours),
      minOutdoorTempBusinessHours: calculateMin(outdoorTempBusinessHours),
      // Power & Temperature fields (not used in energy mode)
      avgPower: 0,
      peakPower: 0,
      minPower: 0,
      avgIndoorTemp: 0,
      maxOutdoorTemp: 0,
      minOutdoorTemp: 0
    };
  } else {
    // Power & Temperature mode statistics - use total_power for overall power stats
    const powerData = metricGroups['total_power'] || [];
    const indoorTempData = metricGroups['indoor_temp'] || [];
    const outdoorTempData = metricGroups['outdoor_temp'] || [];

    return {
      // Energy fields (not used in power-temp mode)
      totalEnergyConsumption: 0,
      totalSolarGeneration: 0,
      totalGridImport: 0,
      avgIndoorTempBusinessHours: 0,
      maxOutdoorTempBusinessHours: 0,
      minOutdoorTempBusinessHours: 0,
      // Power & Temperature fields
      avgPower: calculateAverage(powerData),
      peakPower: calculateMax(powerData),
      minPower: calculateMin(powerData),
      avgIndoorTemp: calculateAverage(indoorTempData),
      maxOutdoorTemp: calculateMax(outdoorTempData),
      minOutdoorTemp: calculateMin(outdoorTempData)
    };
  }
}

function sumMetricValues(data: AnalyticsDataPoint[]): number {
  return data.reduce((sum, point) => sum + point.value, 0);
}

function calculateAverage(data: AnalyticsDataPoint[]): number {
  if (data.length === 0) return 0;
  return data.reduce((sum, point) => sum + point.value, 0) / data.length;
}

function calculateMax(data: AnalyticsDataPoint[]): number {
  if (data.length === 0) return 0;
  return Math.max(...data.map(point => point.value));
}

function calculateMin(data: AnalyticsDataPoint[]): number {
  if (data.length === 0) return 0;
  return Math.min(...data.map(point => point.value));
}

function filterBusinessHours(data: AnalyticsDataPoint[]): AnalyticsDataPoint[] {
  return data.filter(point => {
    const hour = new Date(point.timestamp).getHours();
    return hour >= 9 && hour <= 17;
  });
}

export function formatDateForAPI(date: string, isEndDate: boolean = false): string {
  const dateObj = new Date(date);
  
  if (isEndDate) {
    // Set to end of day (23:59:59)
    dateObj.setHours(23, 59, 59, 999);
  } else {
    // Set to start of day (00:00:00)
    dateObj.setHours(0, 0, 0, 0);
  }
  
  // Format with +07:00 timezone for Asia/Bangkok
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+07:00`;
}

export function getDefaultDateRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 1); // Default to last 1 day
  
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
}

/**
 * Aggregate hourly data to daily sums for energy mode
 */
export function aggregateToDaily(data: AnalyticsDataPoint[]): AnalyticsDataPoint[] {
  if (!data.length) return [];

  // Group data by date and metric
  const dailyGroups = new Map<string, Map<string, number[]>>();
  
  data.forEach(point => {
    // Extract date from timestamp (YYYY-MM-DD)
    const date = point.timestamp.split('T')[0];
    
    if (!dailyGroups.has(date)) {
      dailyGroups.set(date, new Map());
    }
    
    const dayData = dailyGroups.get(date)!;
    if (!dayData.has(point.metric)) {
      dayData.set(point.metric, []);
    }
    
    dayData.get(point.metric)!.push(point.value);
  });

  // Convert to daily aggregated data
  const aggregatedData: AnalyticsDataPoint[] = [];
  
  console.log(`Aggregating ${dailyGroups.size} days of data`);
  
  dailyGroups.forEach((metricsMap, date) => {
    console.log(`Day ${date} has ${metricsMap.size} metrics`);
    metricsMap.forEach((values, metric) => {
      // Check if this is a power metric or temperature metric
      const isPowerMetric = POWER_METRICS.some(m => m.key === metric);
      const isTemperatureMetric = TEMPERATURE_METRICS.some(m => m.key === metric);
      
      let aggregatedValue: number;
      
      if (isPowerMetric) {
        // Sum power metrics for energy
        aggregatedValue = values.reduce((sum, val) => sum + val, 0);
      } else if (isTemperatureMetric) {
        // Average temperature metrics
        aggregatedValue = values.reduce((sum, val) => sum + val, 0) / values.length;
      } else {
        // Default to sum for unknown metrics
        aggregatedValue = values.reduce((sum, val) => sum + val, 0);
      }
      
      // Create timestamp for the day at midnight
      const timestamp = `${date}T00:00:00+07:00`;
      
      aggregatedData.push({
        timestamp,
        value: aggregatedValue,
        metric
      });
    });
  });

  return aggregatedData;
}